import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  AutoAwesome as AiIcon,
  Campaign as ExecBriefIcon,
  SwapHoriz as HandoffIcon,
} from '@mui/icons-material';

import { dailyPlanApi } from '../../api/services';
import type { DailyPlan } from '../../types/models';

// ---------------------------------------------------------------------------
// DailyBriefingDialog — one dialog, three clearly-labelled surfaces:
//
//   1. AI briefing (read-only) — what Start My Day generated.
//   2. Executive briefing (editable) — the EA's narrative for the exec, or
//      the exec's own framing. MCP clients can also write this via the
//      backend's update_plan_notes tool (extended to carry executiveBriefing).
//   3. Handoff notes (editable) — the EA-to-cover-EA handover. Separate from
//      the exec briefing because the audience is different.
//
// Each editable field has its own save button to avoid accidentally
// publishing changes to a field the user didn't touch.
// ---------------------------------------------------------------------------

interface DailyBriefingDialogProps {
  open: boolean;
  onClose: () => void;
  date: string;
  plan: DailyPlan | null;
  forExecId?: string;
  onUpdated?: (updated: DailyPlan) => void;
}

export default function DailyBriefingDialog({
  open,
  onClose,
  date,
  plan,
  forExecId,
  onUpdated,
}: DailyBriefingDialogProps) {
  const [executiveBriefing, setExecutiveBriefing] = useState('');
  const [handoffNotes, setHandoffNotes] = useState('');
  const [saving, setSaving] = useState<'exec' | 'handoff' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setExecutiveBriefing(plan?.executiveBriefing ?? '');
    setHandoffNotes(plan?.handoffNotes ?? '');
    setError(null);
    setSavedMessage(null);
  }, [open, plan?.executiveBriefing, plan?.handoffNotes]);

  const hasPlan = Boolean(plan);
  const canSaveExec = hasPlan && executiveBriefing !== (plan?.executiveBriefing ?? '');
  const canSaveHandoff = hasPlan && handoffNotes !== (plan?.handoffNotes ?? '');

  const handleSaveExec = async () => {
    if (!plan) return;
    setSaving('exec');
    setError(null);
    try {
      const updated = await dailyPlanApi.updateExecutiveBriefing(date, executiveBriefing, forExecId);
      setSavedMessage('Executive briefing saved.');
      onUpdated?.(updated);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save the executive briefing.');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveHandoff = async () => {
    if (!plan) return;
    setSaving('handoff');
    setError(null);
    try {
      const updated = await dailyPlanApi.updateHandoffNotes(date, handoffNotes, forExecId);
      setSavedMessage('Handoff notes saved.');
      onUpdated?.(updated);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save the handoff notes.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={saving !== null ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Daily briefing</Typography>
            {plan?.dailyTheme && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                {plan.dailyTheme}
              </Typography>
            )}
          </Box>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />} disabled={saving !== null}>
            Close
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!plan && (
          <Alert severity="info">
            No plan has been generated yet. Run "Start my day" to produce a briefing, or write notes after the plan exists.
          </Alert>
        )}

        {plan && (
          <Stack spacing={2.5}>
            {/* AI briefing — read-only. */}
            {plan.aiBriefing && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <AiIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2">AI briefing</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                >
                  {plan.aiBriefing}
                </Typography>
              </Box>
            )}

            {plan.aiRisks && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  Risks
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {plan.aiRisks}
                </Typography>
              </Box>
            )}

            {(plan.aiBriefing || plan.aiRisks) && <Divider />}

            {/* Executive briefing — editable, EA-for-exec or exec-for-self. */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <ExecBriefIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="subtitle2">Executive briefing</Typography>
                {plan.preparedBy && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Prepared by {plan.preparedBy}
                  </Typography>
                )}
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={4}
                value={executiveBriefing}
                onChange={(e) => setExecutiveBriefing(e.target.value)}
                placeholder="Narrative briefing written for the executive. MCP clients can update this too."
                disabled={saving !== null}
                size="small"
              />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveExec}
                  disabled={!canSaveExec || saving !== null}
                  startIcon={saving === 'exec' ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                >
                  {saving === 'exec' ? 'Saving…' : 'Save briefing'}
                </Button>
              </Stack>
            </Box>

            <Divider />

            {/* Handoff notes — editable, EA-to-cover-EA context. */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <HandoffIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2">Handoff notes</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  For a cover EA or the next shift
                </Typography>
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={handoffNotes}
                onChange={(e) => setHandoffNotes(e.target.value)}
                placeholder="Context for a cover EA: watch-outs, pending actions, people still chasing."
                disabled={saving !== null}
                size="small"
              />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSaveHandoff}
                  disabled={!canSaveHandoff || saving !== null}
                  startIcon={saving === 'handoff' ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                >
                  {saving === 'handoff' ? 'Saving…' : 'Save handoff'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {savedMessage && !error && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSavedMessage(null)}>
            {savedMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving !== null}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
